import { ChangeEvent, useEffect, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { theme } from '@frontend/theme';
import { Button, Chip, useTooltip } from '@frontend/design-system';
import { recentCallsTrackingId } from '../pendo-tracking';

export const RecentCallsTagSelector = ({
  existingTagIds = [],
  onClose,
  onSelect,
  onApplyTag,
}: {
  existingTagIds?: string[];
  onClose?: () => void;
  onSelect?: (tag: Tag) => void;
  onApplyTag?: (tag: Tag) => void;
}) => {
  const [tagSearchValue, setTagSearchValue] = useState('');
  const { navigate } = useSettingsNavigate();

  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    onClose: () => {
      closeTagSelection();
      onClose?.();
    },
    onTagSelect: async (tag) => {
      closeTagSelection();
      onApplyTag?.(tag);
      onSelect?.(tag);
    },
    onTagCreate: (initName) => {
      navigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => {
            onApplyTag?.(tag);
          },
          name: initName ? encodeURIComponent(initName) : '',
        },
      });
    },
    trackingIds: {
      selectTag: recentCallsTrackingId({ context: 'select-tag' }),
      createTag: recentCallsTrackingId({ context: 'create-tag' }),
      createNamedTag: recentCallsTrackingId({ context: 'create-named-tag' }),
      openTagSettings: recentCallsTrackingId({ context: 'open-tag-settings' }),
    },
    searchValue: tagSearchValue,
    tagIdsToExclude: [...existingTagIds, ''],
  });

  useEffect(() => {
    openTagSelection();
  }, [openTagSelection]);

  return (
    <span ref={getTagSelectionTriggerProps().ref}>
      <Chip.Tag
        editable
        color='gray'
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            closeTagSelection();
            onClose?.();
          }
          if (e.key === 'Enter' && tagSelectionIsOpen) {
            e.preventDefault();
            if (tagPopoverProps.tagsList[0]) {
              closeTagSelection();
              const tag = tagPopoverProps.tagsList[0];
              onApplyTag?.(tag);
              onSelect?.(tag);
            } else {
              navigate({
                to: '/tags/quick-create',
                context: { name: tagSearchValue ? encodeURIComponent(tagSearchValue) : '' },
              });
            }
          }
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setTagSearchValue(e.currentTarget.value);
        }}
      />
      <TagSelectionComponents.TagSelectionPopover {...tagPopoverProps} returnFocus={false} initialFocus={-1} />
    </span>
  );
};

export const AddTag = ({
  existingTagIds = [],
  onApplyTag,
}: {
  existingTagIds?: string[];
  onApplyTag?: (tag: Tag) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation('calls');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return isEditing ? (
    <RecentCallsTagSelector
      existingTagIds={existingTagIds}
      onClose={() => {
        setIsEditing(false);
      }}
      onSelect={() => {
        setIsEditing(false);
      }}
      onApplyTag={onApplyTag}
    />
  ) : (
    <>
      <Button
        {...triggerProps}
        variant='secondary'
        css={{
          padding: theme.spacing(0.5),
          border: 'none',
        }}
        label={t('Tag Voicemail')}
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        trackingId='phone-portal-weave2.0-recentCalls-btn-tablecolumnaction-tagrecords'
      >
        <Icon name='label' color='light' />
      </Button>
      <Tooltip {...tooltipProps}>{t('Add tag')}</Tooltip>
    </>
  );
};
